import { ClientOngoingLoad, DeliveryStatusType, isPreStartOngoingLoadStatus, OngoingLoadStatus } from '@numeo/types-core'
import { isStoredEtaLate } from '@numeo/types-main-web-app'
import { formatDeliveryStatus, getDeliveryStatusColor } from 'utils/delivery-status'

/** Loads that are done moving — driver/truck reassignment, and live truck tracking, are no longer meaningful. */
export const isDeliveredLoadStatus = (status?: OngoingLoadStatus): boolean => status === OngoingLoadStatus.COMPLETED || status === OngoingLoadStatus.CANCELLED

type CorrectableLoad = { status?: OngoingLoadStatus; isSplitLoad?: boolean; payrollLocked?: boolean }

/**
 * A completed load whose truck/driver can still be CORRECTED from the Delivered tab (Reassign). This is a
 * fix to the record, not a dispatch: the server keeps the load completed and sends nothing. A split load is
 * excluded — its per-driver legs are payroll input and the server refuses to rebuild them from a truck. So
 * is a load a locked or settled statement carries: that pay is final, and the server would refuse it with a
 * 409 only after the dispatcher had already picked a truck. `payrollLocked` can lag a statement that
 * changed elsewhere, so the server's own check stays the backstop.
 */
export const isCompletedLoadCorrectable = (load: CorrectableLoad): boolean =>
    load.status === OngoingLoadStatus.COMPLETED && !load.isSplitLoad && !load.payrollLocked

/** Why a completed load's truck cannot be corrected, for the tooltip on its assignment. `undefined` when it can. */
export const getCompletedLoadLockReason = (load: CorrectableLoad): string | undefined => {
    if (load.status !== OngoingLoadStatus.COMPLETED) return undefined
    if (load.payrollLocked) return "This load's payroll statement is locked or settled, so its truck and driver can't be changed."
    if (load.isSplitLoad) return 'Revert the split before changing the truck on this load'
    return undefined
}

/**
 * Whether the selection holds a load a locked or settled statement carries. Such a load can't be deleted:
 * the statement would keep paying for a load that no longer exists. The server refuses it too, and its live
 * lookup also catches a statement that settled after this page loaded.
 */
export const hasPayrollLockedLoadSelected = (loads: readonly Pick<ClientOngoingLoad, 'id' | 'payrollLocked'>[], selectedIds: ReadonlySet<string>): boolean =>
    selectedIds.size > 0 && loads.some((load) => selectedIds.has(load.id) && load.payrollLocked === true)

export const PAYROLL_LOCKED_DELETE_REASON = "A selected load is on a locked or settled payroll statement, so it can't be deleted"

/**
 * Loads whose split can be configured. Mirrors `isSplitConfigurableStatus` on the server, which is what
 * the split / revert-split / split-details endpoints enforce — keep the two in step so the UI never offers
 * an action the API will reject.
 *
 * UPCOMING is included so a hand-off can be planned before dispatch, and so a load pulled back out of
 * COMPLETED can still be fixed.
 */
export const isSplitConfigurableStatus = (status?: OngoingLoadStatus): boolean =>
    status === OngoingLoadStatus.IN_TRANSIT || status === OngoingLoadStatus.UPCOMING || status === OngoingLoadStatus.DISPATCHED

/**
 * Loads whose ETA the board shows: only a load on the road. A dispatched load has none: the truck may still
 * be finishing another load, so its ETA starts when it moves to In Transit.
 */
export const isEtaShownForStatus = (status: OngoingLoadStatus | undefined): boolean => status === OngoingLoadStatus.IN_TRANSIT

/**
 * Whether the board paints the load as late. The ETA label's text says "late" straight from
 * `eta.isLate`, so whenever that label is on screen its colour follows the same flag — for every
 * status and delivery status, never green beside a "late". Without a label, an En Route load the
 * ETA marks late is still painted late, as it always was. The late flag itself comes from the shared
 * owner (`isStoredEtaLate`, the flag `getLoadTimeliness` reads); only the on-screen gating lives here.
 */
export const isLoadEtaLate = (load: Pick<ClientOngoingLoad, 'eta' | 'deliveryStatus'>, activeTab: string, isEtaLabelShown: boolean): boolean => {
    if (!isStoredEtaLate(load.eta) || activeTab === 'history') return false
    return isEtaLabelShown || load.deliveryStatus?.[load.deliveryStatus.length - 1]?.status === DeliveryStatusType.EN_ROUTE
}

/**
 * While a load is still Pending before its pickup, the chip names the pre-start step instead: Dispatched
 * with the Dispatched stage on, and Prebook for any other load that already has its truck, flag or not
 * (with the stage off a dispatched load is shown as the upcoming load it is treated as). Once the delivery
 * status has moved past Pending (an UPCOMING load can be En Route or Arrived), the chip shows that instead.
 */
export const getPreStartChipLabel = (
    load: Pick<ClientOngoingLoad, 'status' | 'truckId' | 'deliveryStatus'>,
    isDispatchedStageEnabled: boolean
): string | undefined => {
    if (!isPreStartOngoingLoadStatus(load.status)) return undefined
    const lastDeliveryStatus = load.deliveryStatus?.[load.deliveryStatus.length - 1]?.status
    if (lastDeliveryStatus && lastDeliveryStatus !== DeliveryStatusType.PENDING) return undefined
    if (isDispatchedStageEnabled && load.status === OngoingLoadStatus.DISPATCHED) return 'Dispatched'
    return load.truckId ? 'Prebook' : undefined
}

/**
 * The name the Ongoing Loads table puts on a load's status chip, so every screen names a load's status the same way: the pre-start step
 * (Prebook, Dispatched) while the load has not moved, else the latest delivery status with the stop it is about ("En Route 1").
 * `undefined` when the load has no delivery status yet and is not in a pre-start step. `bracket` is the classic board's "En Route (1)".
 */
export const getLoadStatusChipLabel = (
    load: Pick<ClientOngoingLoad, 'status' | 'truckId' | 'deliveryStatus'>,
    isDispatchedStageEnabled: boolean,
    stopNumberFormat: 'bare' | 'bracket' = 'bare'
): string | undefined => {
    const preStart = getPreStartChipLabel(load, isDispatchedStageEnabled)
    if (preStart) return preStart
    const lastEntry = load.deliveryStatus?.[load.deliveryStatus.length - 1]
    if (!lastEntry) return undefined
    const stopNumber = typeof lastEntry.stopIndex === 'number' ? lastEntry.stopIndex + 1 : null
    const stop = stopNumber === null ? '' : stopNumberFormat === 'bare' ? ` ${stopNumber}` : ` (${stopNumber})`
    return `${formatDeliveryStatus(lastEntry.status)}${stop}`
}

export type LoadStatusTone = 'accent' | 'success' | 'warning' | 'danger' | 'neutral'

/**
 * The color the Ongoing Loads table gives a load's status chip: that of the latest delivery status (Completed green, En Route blue, Arrived
 * amber, Cancelled red, Pending gray), so a chip named the way the table names it is also colored the way the table colors it.
 * `undefined` when the load has no delivery status yet.
 */
export const getLoadStatusChipTone = (load: Pick<ClientOngoingLoad, 'deliveryStatus'>): LoadStatusTone | undefined => {
    const lastEntry = load.deliveryStatus?.[load.deliveryStatus.length - 1]
    if (!lastEntry) return undefined
    const color = getDeliveryStatusColor(lastEntry.status)
    return color === 'primary' ? 'accent' : (color as LoadStatusTone)
}
